import { yupResolver } from '@hookform/resolvers/yup';
import { FormContainer } from '@openx/components-core/lib/FormContainer/FormContainer';
import { scrollToFirstErrorInForm } from '@openx/components-core/lib/utils/scrollToFirstErrorInForm';
import { onKeyDown } from '@openx/components-form/onKeyDown';
import { PageHeader } from '@openx/components-header/PageHeader';
import type { InventoryListFormState } from '@openx/types/inventoryList';
import type { FormEvent } from 'react';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InventoryListsFormFields } from './InventoryListsFormFields/InventoryListsFormFields';
import { inventoryListValidationSchema } from './validationSchema';

type InventoryListsFormProps = {
  onSubmit: (values: InventoryListFormState) => void;
  initialValues: InventoryListFormState;
  isEdit: boolean;
  loading: boolean;
};

export const InventoryListsForm = ({ onSubmit, initialValues, isEdit, loading }: InventoryListsFormProps) => {
  const { t } = useTranslation();

  const formMethods = useForm<InventoryListFormState>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: yupResolver(inventoryListValidationSchema),
  });

  const onFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      const formElement = event.currentTarget;

      void formMethods.handleSubmit(onSubmit, errors => {
        /* RHFTextField disables its input while formState.isSubmitting is true, and RHF hasn't reset
        that flag yet at this point even on validation failure — focusing the field before the reset
        commits is a no-op on a disabled input, so we wait a tick (as in Form.tsx) before scrolling.
         */
        setTimeout(() => {
          scrollToFirstErrorInForm(errors, {
            root: formElement,
          });
        }, 100);
      })(event);
    },
    [formMethods, onSubmit]
  );

  return (
    <PageHeader titlePrefix="OpenXSelect" title={t('{formMode} Allow List', { formMode: isEdit ? 'Edit' : 'Create' })}>
      <FormContainer fullPage>
        <FormProvider {...formMethods}>
          <form noValidate onSubmit={onFormSubmit} onKeyDown={onKeyDown}>
            <InventoryListsFormFields isEdit={isEdit} loading={loading} />
          </form>
        </FormProvider>
      </FormContainer>
    </PageHeader>
  );
};
